import React, { useEffect, useState } from "react";
import Sidebar from "./Sidebar";
import Timeline from "./Timeline";
import AuthPanel from "./components/AuthPanel";
import ErrorBanner from "./components/ErrorBanner";
import { supabase } from "./api/supabase";
import { createProject, deleteProject, listProjects } from "./api/projects";

const App = () => {
  const [session, setSession] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let isMounted = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!isMounted) return;
      setSession(data.session ?? null);
      setAuthLoading(false);
    });

    const { data: authListener } = supabase.auth.onAuthStateChange(
      (_event, newSession) => {
        if (!isMounted) return;
        setSession(newSession);
        setAuthLoading(false);
      }
    );

    return () => {
      isMounted = false;
      authListener.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!session) {
      setProjects([]);
      setSelectedProjectId(null);
      return;
    }

    const loadProjects = async () => {
      setError("");
      try {
        const data = await listProjects();
        setProjects(data);
      } catch (err) {
        setError(err.message || "Failed to load projects");
      }
    };

    loadProjects();
  }, [session]);

  const handleCreateProject = async () => {
    if (!session?.user?.id) return;
    setError("");
    try {
      const project = await createProject({
        title: "New Project",
        userId: session.user.id,
      });
      setProjects((prev) => [project, ...prev]);
      setSelectedProjectId(project.id);
    } catch (err) {
      setError(err.message || "Failed to create project");
    }
  };

  const handleDeleteProject = async (projectId) => {
    setError("");
    try {
      await deleteProject(projectId);
      setProjects((prev) => prev.filter((project) => project.id !== projectId));
      if (selectedProjectId === projectId) {
        setSelectedProjectId(null);
      }
    } catch (err) {
      setError(err.message || "Failed to delete project");
    }
  };

  const handleProjectUpdated = (updatedProject) => {
    setProjects((prev) => {
      const exists = prev.some((project) => project.id === updatedProject.id);
      if (!exists) return [updatedProject, ...prev];
      return prev.map((project) =>
        project.id === updatedProject.id ? { ...project, ...updatedProject } : project
      );
    });
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
  };

  if (authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-canvas">
        <div className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">
          Loading…
        </div>
      </div>
    );
  }

  if (!session) {
    return <AuthPanel />;
  }

  return (
    <div className="flex min-h-screen bg-canvas text-zinc-900">
      <Sidebar
        projects={projects}
        onSelectProject={setSelectedProjectId}
        onDeleteProject={handleDeleteProject}
        onCreateProject={handleCreateProject}
        selectedProjectId={selectedProjectId}
        userEmail={session.user.email}
        onSignOut={handleSignOut}
      />
      <main className="flex-1 min-w-0">
        {error && (
          <div className="px-8 pt-6">
            <ErrorBanner error={error} />
          </div>
        )}
        <Timeline
          projectId={selectedProjectId}
          userId={session.user.id}
          onProjectUpdated={handleProjectUpdated}
        />
      </main>
    </div>
  );
};

export default App;
